import { Users, Plus, Trash2 } from "lucide-react";
import { getSections, formatAnswer } from "@/data/questionnaire";
import { getStaffQuestions, compareValues } from "@/data/staff";

const fmt = (q, v) => formatAnswer(q, v) || "—";

const Tag = ({ children, tone }) => (
  <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold mr-1.5 mb-1.5 ${tone === "ok" ? "bg-[#2E6B40]/10 text-[#2E6B40]" : tone === "owner" ? "bg-[#C85A32]/10 text-[#A64B2A]" : "bg-[#2D4030]/10 text-[#2D4030]"}`}>{children}</span>
);

const Compare = ({ q, owner, person, ownerQ }) => {
  const oq = ownerQ[q.compareTo];
  const ov = owner[q.compareTo];
  const sv = person.answers[q.id];
  const c = compareValues(q.type, ov, sv);
  if (q.type === "multiple")
    return (
      <div className="text-sm">
        {c.common.map((x) => <Tag key={x} tone="ok">✓ {x}</Tag>)}
        {c.onlyOwner.map((x) => <Tag key={x} tone="owner">solo titolari: {x}</Tag>)}
        {c.onlyStaff.map((x) => <Tag key={x} tone="staff">solo {person.name}: {x}</Tag>)}
        {c.common.length + c.onlyOwner.length + c.onlyStaff.length === 0 && <span className="italic text-[#B0A096]">Nessuna risposta</span>}
      </div>
    );
  return (
    <div className="grid sm:grid-cols-2 gap-3 text-sm">
      <div className="rounded-xl bg-[#FBF1EA] p-3"><p className="eyebrow !text-[10px]">Titolari</p><p className="text-[#2C221E] mt-1">{fmt(oq, ov)}</p></div>
      <div className={`rounded-xl p-3 ${q.type === "single" ? (c.same ? "bg-[#2E6B40]/10" : "bg-[#D99B26]/15") : "bg-[#F1F4EC]"}`}>
        <p className="eyebrow !text-[10px]">{person.name} {q.type === "single" && (c.same ? "· d'accordo" : "· vede diversamente")}</p>
        <p className="text-[#2C221E] mt-1">{fmt(q, sv)}</p>
      </div>
    </div>
  );
};

export const StaffComparison = ({ sub, onAdd, onRemove, onResume }) => {
  const staff = sub.staff || [];
  const completed = staff.filter((p) => p.status === "completed");
  const questions = getStaffQuestions(sub.businessType);
  const ownerQ = Object.fromEntries(getSections(sub.businessType).flatMap((s) => s.questions).map((q) => [q.id, q]));
  return (
    <section className="card p-6 sm:p-9" data-testid="staff-comparison">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Users className="text-[#2D4030]" size={24} />
          <div>
            <p className="eyebrow">Confronto</p>
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#382E2B]">Titolari e staff</h2>
          </div>
        </div>
        <button className="btn btn-ghost !min-h-[44px] text-sm no-print" onClick={onAdd} data-testid="add-staff-btn"><Plus size={16} /> Aggiungi voce dello staff</button>
      </div>

      {staff.length === 0 && <p className="text-[#7A6B63] mt-5">Nessuna voce dello staff ancora. Passa il tablet a una persona di sala o cucina: 10 domande, cinque minuti.</p>}

      {staff.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-5" data-testid="staff-list">
          {staff.map((p) => (
            <div key={p.id} className="flex items-center gap-2 rounded-full border border-[#E3D5C3] bg-white pl-4 pr-1 py-1 text-sm" data-testid={`staff-chip-${p.id}`}>
              <span className="font-semibold text-[#2C221E]">{p.name}</span>
              <span className="text-[#7A6B63]">{p.role}</span>
              {p.status !== "completed" && <button className="text-[#C85A32] underline underline-offset-2 no-print" onClick={() => onResume(p)} data-testid="resume-staff-btn">riprendi</button>}
              <button className="w-8 h-8 grid place-items-center rounded-full text-[#8A7970] hover:bg-[#F3ECE1] no-print" onClick={() => onRemove(p)} aria-label="Elimina" data-testid="remove-staff-btn"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}

      {completed.length > 0 && (
        <div className="grid gap-7 mt-8">
          {questions.map((q) => (
            <div key={q.id} data-testid={`compare-${q.id}`}>
              <p className="font-semibold text-[#2C221E] leading-snug mb-3">{q.text}</p>
              <div className="grid gap-3">
                {completed.map((p) => (
                  q.compareTo ? (
                    <Compare key={p.id} q={q} owner={sub.answers} person={p} ownerQ={ownerQ} />
                  ) : (
                    <div key={p.id} className="rounded-xl bg-[#F1F4EC] p-3 text-sm"><p className="eyebrow !text-[10px]">{p.name}</p><p className="text-[#2C221E] mt-1">{fmt(q, p.answers[q.id])}</p></div>
                  )
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
